<template>
  <div>
    <template v-if="ready">
      <div class="d-flex justify-content-between align-items-center px-2">
        <h5 class="mb-2">{{ $t("bau_complement.select_form") }}</h5>
      </div>

      <div class="m-2">
        <v-select
          v-model="itemData.templates"
          :options="filteredTemplates"
          :reduce="(template) => template.id"
          label="label"
          :placeholder="$t('bau_complement.select_form_place_holder')"
          multiple
        />
      </div>

      <div class="mt-2" v-if="selectedTemplates.length">
        <div class="d-flex justify-content-between align-items-center">
          <h5>{{ $t("bau_complement.selected_form") }}</h5>
          <b-button
            variant="secondary"
            @click="onAnswerClick"
            :disabled="!mergedSnapshot.length || answering"
          >
            {{ answering ? $t("loading") : $t("bau_complement.answer_form") }}
          </b-button>
        </div>

        <div class="mt-2">
          <h6 class="mt-1">{{ $tc("simulacrum.template.question", 2) }}</h6>

          <div v-if="!mergedSnapshot.length" class="text-muted small">
            {{ $t("loading") }}
          </div>

          <ul v-else>
            <li
              v-for="(question, index) in previewQuestions"
              :key="question.item_id || index"
              class="mb-2"
            >
              <p class="mb-1">
                <strong>{{ $tc("simulacrum.template.question", 1) + "-" + (index + 1) }}:</strong>
                <span class="text-muted">
                  [{{ question.section || $t("simulacrum.template.no_section") }}]
                </span>
                {{ question.name }}
              </p>
              <p class="mb-1">
                <strong>{{ $tc("custom_forms.types.title", 2) }}:</strong>
                {{ question.type }}
              </p>
              <template
                v-if="
                  question.options &&
                  question.options.fieldTypeCfg &&
                  question.options.fieldTypeCfg.data &&
                  question.options.fieldTypeCfg.data.length
                "
              >
                <p class="mb-1"><strong>{{ $t("simulacrum.template.options") }}:</strong></p>
                <ul class="mb-0">
                  <li
                    v-for="(option, optionIndex) in question.options.fieldTypeCfg.data"
                    :key="optionIndex"
                  >
                    {{ option.name }}
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div class="text-center" v-else>
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import axios from "@/libs/axios";
import vSelect from "vue-select";
import { BButton, BSpinner } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import { shouldUpdateExistingRecordToInProgress } from "@/libs/risk/scheduledStartFormAccess";

export default {
  name: "BauComplementAssessmentStep",
  components: { vSelect, BButton, BSpinner },
  props: {
    propItemData: { type: Object, required: true },
    existingBauComplementId: { type: Number, default: null },
    existingRecordLastStatusName: { type: String, default: null },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const itemData = ref({
      ...props.propItemData,
      templates: [],
      reference_number:
        props.propItemData?.reference_number || `BAU-${new Date().getFullYear().toString().slice(-2)}`,
    });
    const ready = ref(false);
    const answering = ref(false);
    const templates = ref([]);
    const mergedSnapshot = ref([]);

    const fetchTemplates = async () => {
      try {
        const { data } = await axios.get("/v1/bau_complement_templates");
        templates.value = data.data || [];
      } catch (_) {
        toast.error("Error al cargar los templates");
      }
    };

    const filteredTemplates = computed(() => {
      const tenantId = itemData.value.id_tenant;
      const list = templates.value || [];
      const byTenant = list.filter((template) => Number(template.id_tenant) === Number(tenantId));
      const source = byTenant.length ? byTenant : list;
      return source.map((template) => ({ id: template.id, label: template.name }));
    });

    const selectedTemplates = computed(() => {
      const selectedIds = new Set(itemData.value.templates || []);
      const byId = new Map((templates.value || []).map((template) => [template.id, template]));
      return [...selectedIds].map((id) => byId.get(id)).filter(Boolean);
    });

    const deepClone = (value) => JSON.parse(JSON.stringify(value));
    const namespaceFieldIds = (templateId, fields) =>
      (Array.isArray(fields) ? fields : []).map((field) => {
        const normalizedField = deepClone(field);
        const oldId = normalizedField.item_id;
        normalizedField.item_id = `${templateId}:${oldId}`;
        if (Array.isArray(normalizedField.show_if)) {
          normalizedField.show_if = normalizedField.show_if.map((condition) =>
            condition && condition.field
              ? { ...condition, field: `${templateId}:${condition.field}` }
              : condition
          );
        }
        return normalizedField;
      });

    const mergeTemplatesToSnapshot = (templatesToMerge) => {
      const merged = [];
      (templatesToMerge || []).forEach((template) => {
        const data = Array.isArray(template.data) ? template.data : [];
        namespaceFieldIds(template.id, data).forEach((field) => merged.push(field));
      });
      return merged;
    };

    const recomputeMergedSnapshot = () => {
      const selected = selectedTemplates.value;
      mergedSnapshot.value = selected.length ? mergeTemplatesToSnapshot(selected) : [];
    };

    watch(
      () => itemData.value.templates,
      (newValue, oldValue) => {
        if (newValue !== oldValue) recomputeMergedSnapshot();
      }
    );

    const createBauComplement = async () => {
      const payload = {
        reference_number: itemData.value.reference_number,
        id_unit: itemData.value.id,
        evaluation_date: new Date().toISOString(),
      };
      const response = await axios.post("/v1/bau-complements", payload);
      return response.data?.data?.id;
    };

    const resolveBauComplementId = async () => {
      if (
        props.existingBauComplementId != null &&
        Number.isFinite(Number(props.existingBauComplementId))
      ) {
        return Number(props.existingBauComplementId);
      }
      return createBauComplement();
    };

    const isExistingScheduledFlow = () =>
      props.existingBauComplementId != null &&
      Number.isFinite(Number(props.existingBauComplementId));

    const updateStatusToInProgress = async (bauComplementId) => {
      await axios.post(`/v1/bau-complements/update_status/${bauComplementId}`, {
        status: "in_progress",
      });
    };

    const onAnswerClick = async () => {
      if (!selectedTemplates.value.length || !mergedSnapshot.value.length) return;
      answering.value = true;
      try {
        const id = await resolveBauComplementId();
        if (
          isExistingScheduledFlow() &&
          shouldUpdateExistingRecordToInProgress(props.existingRecordLastStatusName)
        ) {
          await updateStatusToInProgress(id);
        }
        emit("open-bau-complement-modal", {
          id,
          snapshot: mergedSnapshot.value,
          sources: (itemData.value.templates || []).slice(),
        });
        emit("assessment-complete");
      } catch (error) {
        const message =
          error?.response?.data?.message || "No se pudo crear el complemento BAU";
        toast.error(message);
      } finally {
        answering.value = false;
      }
    };

    const previewQuestions = computed(() => mergedSnapshot.value);

    onMounted(async () => {
      await fetchTemplates();
      ready.value = true;
    });

    return {
      ready,
      answering,
      itemData,
      filteredTemplates,
      selectedTemplates,
      mergedSnapshot,
      previewQuestions,
      onAnswerClick,
    };
  },
};
</script>
